import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { INVOICE_NUMBER_MAX_LENGTH } from '../../../../constants';
import { FetchStatus, ModalName } from '../../../../enums';
import { useCloseModal } from '../../../../features/modals';
import {
  useOrganization,
  useRegisterOrganizationDevices,
  useResetRegisterOrganizationDevicesStatus,
} from '../../../../features/organization';
import { Button, Input, Modal } from '../../../common';
import { InvoiceButton } from './components';

export const AddDevicesModal = () => {
  const { t } = useTranslation();
  const {
    status: { registerDevices: status },
  } = useOrganization();
  const registerOrganizationDevices = useRegisterOrganizationDevices();
  const resetOrganizationRegisterDevicesStatus = useResetRegisterOrganizationDevicesStatus();
  const [invoice, setInvoice] = useState('');
  const [completed, setCompleted] = useState(false);
  const closeModal = useCloseModal(ModalName.ADD_DEVICES);

  const isValid = useMemo(() => {
    if (invoice.length < INVOICE_NUMBER_MAX_LENGTH) return false;
    if (!/([^a-zA-Z]*[a-zA-Z]){5}/.test(invoice)) return false;
    if (!/(\D*\d){9}/.test(invoice)) return false;
    return true;
  }, [invoice]);

  const send = useCallback(() => {
    registerOrganizationDevices(invoice);
  }, [registerOrganizationDevices, invoice]);

  useEffect(() => {
    if (status === FetchStatus.SUCCESS) {
      setCompleted(true);
      resetOrganizationRegisterDevicesStatus();
    }
  }, [status]);

  const footer: JSX.Element = useMemo(() => {
    return completed ? (
      <div className="flex">
        <Button text={t('modal.addDevices.button.ok')} onClick={closeModal} />
      </div>
    ) : (
      <div className="flex">
        <Button
          text={t('modal.addDevices.button.send')}
          disabled={!isValid}
          loading={status === FetchStatus.LOADING}
          onClick={send}
        />
        <Button text={t('modal.addDevices.button.cancel')} type="tertiary" onClick={closeModal} className="ml-2" />
      </div>
    );
  }, [closeModal, send, completed, isValid, status]);

  return (
    <Modal
      name={ModalName.ADD_DEVICES}
      header={t(`modal.addDevices.title.${completed ? 'completed' : 'initial'}`)}
      footer={footer}
    >
      <p className="typography-paragraph-3 text-text-secondary">
        {t(`modal.addDevices.description.${completed ? 'completed' : 'initial'}`)}
      </p>

      {!completed && (
        <>
          <Input
            value={invoice}
            label={t('modal.addDevices.field.invoice.label')}
            maxLength={INVOICE_NUMBER_MAX_LENGTH}
            onChange={setInvoice}
            placeholder={t('modal.addDevices.field.invoice.placeholder')}
            helpLabel={t('modal.addDevices.field.invoice.help')}
            errorLabel={t('modal.addDevices.field.invoice.error')}
            hasError={status === FetchStatus.FAILURE}
            className="mt-6 mb-4"
          />
          <span className="typography-paragraph-3 text-text-secondary">
            <Trans i18nKey="modal.addDevices.description.support" components={[<InvoiceButton key="button" />]} />
          </span>
        </>
      )}
    </Modal>
  );
};
